import { useEffect, useState } from 'react';
import { fetchCSRFToken } from 'components/auth/authAPI';

export enum AUTH_STATUS {
  PENDING = 'pending', // auth processing request is in flight, auth status is unknown at this point
  RESOLVED = 'resolved', // auth processing request is resolved, `isAuthenticated` value is settled
}

export type AuthState = {
  status: AUTH_STATUS;
  isAuthenticated: boolean | null;
};

/**
 * A hook verifying auth state on app load
 */
export function useAuthenticationHandler(): {
  isUserAuthenticated: boolean;
  isUserAuthenticationPending: boolean;
} {
  const [authState, setAuthState] = useState<AuthState>({
    status: AUTH_STATUS.PENDING,
    isAuthenticated: null,
  });

  const setAuthStatus = (status: AUTH_STATUS, isAuthenticated: boolean): void => {
    setAuthState({
      status,
      isAuthenticated,
    });
  };

  useEffect(() => {
    async function checkIsUserAuthenticated(): Promise<void> {
      try {
        const xsrfToken = await fetchCSRFToken();
        window.xsrfToken = xsrfToken;
        if (xsrfToken !== 'xsrf_not_logged_in') {
          setAuthStatus(AUTH_STATUS.RESOLVED, true);
        } else {
          setAuthStatus(AUTH_STATUS.RESOLVED, false);
        }
      } catch (err) {
        setAuthState({
          status: AUTH_STATUS.RESOLVED,
          isAuthenticated: false,
        });
      }
    }

    checkIsUserAuthenticated();
  }, []);

  let isUserAuthenticated = authState.isAuthenticated === true;
  let isUserAuthenticationPending = authState.status === AUTH_STATUS.PENDING;

  return { isUserAuthenticated, isUserAuthenticationPending };
}
